import { Box, Stack, styled } from "@mui/material"
import type { DoughnutProps } from "@pharmupp/p3-front-commons"
import { type ComponentProps, Fragment } from "react"
import type { MarketShareSplit } from "../model"

export type DoughnutTooltipProps = NonNullable<
  DoughnutProps<MarketShareSplit>["CustomTooltip"]
>

type DoughnutDatum = ComponentProps<DoughnutTooltipProps>["datum"]

export const MarketShareTooltip: DoughnutTooltipProps = ({ datum }) => {
  if (datum.data.laboratories.length > 1) {
    return <MultipleLabTooltip {...datum} />
  }

  return <SingleLabTooltip {...datum} />
}

const SingleLabTooltip = ({ data, color }: DoughnutDatum) => {
  if (data.laboratories.length === 0) {
    return null
  }

  const lab = data.laboratories[0]
  return (
    <TooltipContainer>
      <TooltipColorBox color={color} />
      <Stack gap={0.5}>
        <span key={lab.name}>
          {lab.name} : {lab.formattedPercentage}
        </span>
      </Stack>
    </TooltipContainer>
  )
}

const MultipleLabTooltip = ({ data, color }: DoughnutDatum) => {
  return (
    <TooltipContainer sx={{ flexDirection: "column", alignItems: "flex-start" }}>
      <Stack direction="row" gap={1} alignItems="center">
        <TooltipColorBox color={color} />
        <Box component="span" fontWeight={500}>
          {data.label} :
        </Box>
      </Stack>
      <Box
        display="grid"
        gridTemplateColumns="repeat(2, 1fr)"
        columnGap={1}
        rowGap={1}
      >
        {data.laboratories.map((lab) => (
          <Fragment key={lab.name}>
            <Box component="span">{lab.name} : </Box>
            <Box component="strong">{lab.formattedPercentage}</Box>
          </Fragment>
        ))}
      </Box>
    </TooltipContainer>
  )
}

const TooltipContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.dark1,
  fontSize: theme.typography.pxToRem(12),
  lineHeight: theme.typography.pxToRem(12),
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  boxShadow: "rgba(0, 0, 0, 0.25) 0px 0px 4px",
  whiteSpace: "pre",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textAlign: "left",
}))

const TooltipColorBox = styled(Box, {
  shouldForwardProp: (prop) => !(prop === "color"),
})<{ color: string }>(({ color }) => ({
  height: "12px",
  width: "12px",
  backgroundColor: color,
}))
